import * as React from "react";
import styled from "styled-components";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { usePrivacy } from "../../../contexts/PrivacyContext";
import UserIcon from "../UserIcon";
import { FiArrowLeft, FiEye, FiEyeOff } from 'react-icons/fi';

const NavItem = ({ label, to, state, replace }) => {
    const location = useLocation();
    const isActive = location.pathname.startsWith(to);

    // Keep the current URL query parameters
    const currentSearch = location.search;
    const linkTo = to + currentSearch;

    return (
        <NavItemWrapper $isActive={isActive}>
            <StyledLink to={linkTo} state={state} replace={replace}>
                {label}
            </StyledLink>
            {isActive && <ActiveIndicator />}
        </NavItemWrapper>
    );
};

const BASE_NAV_ITEMS = [
    { label: "Activities", to: "/admin_activities" },
    { label: "Artifacts", to: "/admin_artifacts" },
    { label: "Team Dynamics", to: "/admin_team_dynamics" },
    { label: "Work Assessment", to: "/admin_work_assessment", requiresDeadline: true },
];

const MyHeader = ({
    groupIndex,
    groups,
    hideNav = false,
    hideControls = false,
    overrideCourseName,
    overrideGroupName,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { groupName, groupInfo, setGroupInfo, courseInfo } = useAuth();
    const { isPrivacyMode, togglePrivacyMode, maskGroupName, maskCourseName } = usePrivacy();
    const navItems = BASE_NAV_ITEMS.filter(
        item => !item.requiresDeadline || courseInfo?.deadlineEnabled !== false
    );
    const currentPath = location.pathname;
    const rawCourseTitle = overrideCourseName || groupInfo?.name || "";
    const courseTitle = maskCourseName(rawCourseTitle, courseInfo?.courseId);
    const currentGroupName = maskGroupName(overrideGroupName ?? groupName ?? "");
    const canShowControls =
        !hideControls &&
        Array.isArray(groups) &&
        groups.length > 0 &&
        Number.isInteger(groupIndex);

    const handlePreClick = async () => {
        if (!canShowControls) return;
        let newGroupIndex;
        if (groupIndex - 1 >= 0) {
            newGroupIndex = groupIndex - 1;
        } else {
            newGroupIndex = groups.length - 1;
        }
        const newGroupInfo = {
            groupId: groups[newGroupIndex].id,
            name: groupInfo.name,
            startDate: groupInfo.startDate
        };
        setGroupInfo(newGroupInfo);
        navigate(currentPath, { state: { groupIndex: newGroupIndex, groups }, replace: true});    
    };
    
    const handleNexClick = () => {
        if (!canShowControls) return;
        let newGroupIndex;
        if (groupIndex + 1 < groups.length) {
            newGroupIndex = groupIndex + 1;
        } else {
            newGroupIndex = 0;
        }

        const newGroupInfo = {
            groupId: groups[newGroupIndex].id,
            name: groupInfo.name,
            startDate: groupInfo.startDate
        };
        setGroupInfo(newGroupInfo);
        navigate(currentPath, { state: { groupIndex: newGroupIndex, groups }, replace: true});
    };

    const handleBackToGroups = () => {
        if (!canShowControls) return;
        navigate(-1);
    };

    return (
        <Header>
            <HeaderWrapper>
                <CourseInfoWrapper>
                    <TopRow>
                        <CourseNameGroup>
                            <CourseName>
                                <StyledLink to={"/admin/home"}>
                                    {courseTitle}
                                </StyledLink>
                            </CourseName>
                            <GroupName>{currentGroupName}</GroupName>
                        </CourseNameGroup>
                        <HeaderRight>
                            <MuteButton
                                onClick={togglePrivacyMode}
                                $active={isPrivacyMode}
                                title={isPrivacyMode ? "Privacy mode on — click to show real names" : "Click to hide student info"}
                            >
                                {isPrivacyMode ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                {isPrivacyMode ? "Privacy On" : "Privacy Off"}
                            </MuteButton>
                            <UserIcon />
                        </HeaderRight>
                    </TopRow>
                    {(!hideNav || canShowControls) && (
                        <NavWrapper>
                            {!hideNav && (
                                <Nav>
                                    {navItems.map((item, index) => (
                                        <NavItem key={index} label={item.label} to={item.to} state={{ groupIndex, groups }} replace={true} />
                                    ))}
                                </Nav>
                            )}
                            {canShowControls && (
                                <RightControls>
                                    <ControlButtons>
                                        <ControlButton onClick={handlePreClick}>
                                            {"<"}
                                        </ControlButton>
                                        <ControlButton onClick={handleNexClick}>
                                            {">"}
                                        </ControlButton>
                                    </ControlButtons>
                                    <BackToGroupsButton onClick={handleBackToGroups}>
                                        <FiArrowLeft size={16} />
                                        <BackToGroupsLabel>Back to Groups</BackToGroupsLabel>
                                    </BackToGroupsButton>
                                </RightControls>
                            )}
                        </NavWrapper>
                    )}
                </CourseInfoWrapper>
            </HeaderWrapper>
        </Header>
    );
};


const NavWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 10px;
  }
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding-left: 20px;

  @media (max-width: 600px) {
    padding-left: 0;
    margin-left: auto;
    gap: 8px;
  }
`;

const BackToGroupsLabel = styled.span`
  @media (max-width: 600px) {
    display: none;
  }
`;

const BackToGroupsButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #fafafc;
  color: #333333;
  border: 3px solid rgba(0, 0, 0, 0.04);
  border-radius: 11px;
  padding: 8px 14px;
  font-family: "SF Pro Text", system-ui, -apple-system, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.29;
  letter-spacing: -0.224px;
  cursor: pointer;
  transition: transform 0.1s ease-in-out;

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 600px) {
    padding: 8px 10px;
    gap: 0;
  }
`;

const ControlButtons = styled.div`
  display: flex;
  gap: 5px;
`;

const ControlButton = styled.button`
  all: unset;
  font-size: 22px;
  width: 33px;
  height: 33px;
  color: #969a97;
  cursor: pointer;
  border-radius: 15px;
  background-color: #f0f0f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #e2e6ea;
    color: #9333ea;
    }

  @media (max-width: 600px) {
    width: 30px;
    height: 30px;
    font-size: 18px;
  }
`;

const StyledLink = styled(Link)`
    all: unset;
    cursor: pointer;
`;

const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
`;

const MuteButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1.5px solid ${props => props.$active ? '#9333ea' : '#d1d5db'};
    background: ${props => props.$active ? '#f3e8ff' : '#fff'};
    color: ${props => props.$active ? '#7e22ce' : '#6b7280'};
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        border-color: #9333ea;
        color: #7e22ce;
        background: #f3e8ff;
    }

    @media (max-width: 600px) {
        padding: 6px 8px;
        font-size: 0;
        gap: 0;
    }
`;

const HeaderWrapper = styled.header`
  display: flex;
  width: 100%;
  gap: 20px;
  color: #000;
  justify-content: space-between;
  padding: 20px 28px;
  box-sizing: border-box;

  @media (max-width: 991px) {
    max-width: 100%;
    flex-wrap: wrap;
  }

  @media (max-width: 600px) {
    padding: 12px 16px;
    gap: 10px;
  }
`;

const CourseInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 8px;
  min-width: 0;
`;

const TopRow = styled.div`
  display: flex;
    width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
  min-width: 0;

  @media (max-width: 600px) {
    margin-bottom: 12px;
  }
`;

const CourseNameGroup = styled.div`
    display: flex;
    //justify-content: space-between;
    align-items: center;
    width: 80%;
    min-width: 0;
    flex: 1;

    @media (max-width: 600px) {
      width: auto;
    }
`;

const CourseName = styled.h1`
    font: 700 30px Inter, sans-serif;
    max-width: 60%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin: 0;

    @media (max-width: 600px) {
      font-size: 20px;
      max-width: 55%;
    }
`;

const GroupName = styled.h2`
    max-width: 40%;
    font: 400 18px Inter, sans-serif;
    text-align: left;
    padding-left: 10px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin: 0;

    @media (max-width: 600px) {
      font-size: 14px;
      padding-left: 8px;
      max-width: 45%;
    }
`;

const Nav = styled.nav`
  display: flex;
  gap: 20px;
  margin-top: 0px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    gap: 14px;
  }
`;

const NavItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${props => props.$isActive ? "#9333ea" : "#969a97"};
  font-size: 18px;
  font-weight: 700;
  font-family: Inter, sans-serif;
  white-space: nowrap;

  @media (max-width: 600px) {
    font-size: 14px;
  }
`;

const ActiveIndicator = styled.div`
  background-color: #9333ea;
  height: 3px;
  width: 100%;
  margin-top: 8px;
`;

const Header = styled.div`
   width: 100%;
  background-color: #fff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  z-index: 100; /* Ensure it's above other elements */
  position: sticky;
  top: 0;
`;

export default MyHeader;
